import { Skeleton, Flex, Spacer, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useMemo } from 'react';

import { CreditFacilityProfitOverviewInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartCards } from '../shared/PortfolioChartCards';
import { PortfolioChartWithCardsTemplateForCreditFacility } from '../shared/PortfolioChartTemplate';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { AxisDomain } from 'recharts/types/util/types';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetProfitOverviewInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';

type ProfitLossOverviewChartTemplateProps = {
  creditFacilityId: string;
  chartTitle: string;
  chartConfig: PortfolioChartConfig<
    keyof CreditFacilityProfitOverviewInsightsGqlDataGQLType
  >;
};
export const ProfitLossOverviewChartTemplate = ({
  creditFacilityId,
  chartTitle,
  chartConfig,
}: ProfitLossOverviewChartTemplateProps) => {
  const toast = useToast();
  const initialActiveFields = getInitialActiveFields({
    chartConfig,
  }) as (keyof CreditFacilityProfitOverviewInsightsGqlDataGQLType)[];
  const [
    selectedBar,
    setSelectedBar,
  ] = useState<CreditFacilityProfitOverviewInsightsGqlDataGQLType | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    CreditFacilityProfitOverviewInsightsGqlDataGQLType[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    CreditFacilityProfitOverviewInsightsGqlDataGQLType[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    (keyof CreditFacilityProfitOverviewInsightsGqlDataGQLType)[]
  >(initialActiveFields);

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<CreditFacilityProfitOverviewInsightsGqlDataGQLType>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  const {
    refetch: refetchProfitOverviewInsights,
    data: profitOverviewInsightsData,
    loading: isProfitOverviewInsightsLoading,
  } = useGetProfitOverviewInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: data.getProfitOverviewInsights,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<CreditFacilityProfitOverviewInsightsGqlDataGQLType>(
          {
            chartData: data.getProfitOverviewInsights,
            selectedTimePeriod,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isProfitOverviewInsightsLoading || !profitOverviewInsightsData) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate<CreditFacilityProfitOverviewInsightsGqlDataGQLType>(
      {
        chartData: chartDataForDateRange,
        selectedTimePeriod: timePeriod,
      },
    );

    setChartDataWithTimePeriod(formattedChartData);
  };

  // custom: when percentExposureDaysPastDue0 chart component is toggled active, we will have the max height of the chart be 100%
  const yAxisDomain: AxisDomain = _.includes(
    activeFields,
    'percentExposureDaysPastDue0',
  )
    ? [0, 1]
    : [0, 'auto'];

  return (
    <PortfolioChartWithCardsTemplateForCreditFacility
      creditFacilityId={creditFacilityId}
      chartTitle={chartTitle}
      refetch={refetchProfitOverviewInsights}
      chartDataDateRange={chartDataDateRange}
      setChartDataDateRange={setChartDataDateRange}
      selectedTimePeriod={selectedTimePeriod}
      onSelectTimePeriod={onSelectTimePeriod}
    >
      {/* child 1 */}
      <PortfolioReChart
        chartData={chartDataWithTimePeriod}
        selectedTimePeriod={selectedTimePeriod}
        currentSelectedBarReportingDate={
          selectedBar
            ? selectedBar.reportingDate
            : getDefaultDashboardDisplayEndDate()
        }
        onSelectBar={setSelectedBar}
        chartConfig={chartConfig}
        visibleChartComponents={activeFields}
        leftYAxisDomain={yAxisDomain}
      />

      {/* child 2 */}

      {selectedBar && (
        <Flex width="25%" flexDirection="column">
          <PortfolioChartCards
            selectedBarData={selectedBar}
            chartConfig={chartConfig}
            activeFields={activeFields}
            onToggleActiveField={(selectedField) =>
              onToggleActiveField({
                selectedField,
                currentActiveFields: activeFields,
                setActiveFields,
              })
            }
          />
          <Spacer />

          <PortfolioDownloadCsvLink
            dataRows={dataRows}
            headers={headers}
            chartTitle={chartTitle}
            selectedTimePeriod={selectedTimePeriod}
          />
        </Flex>
      )}
    </PortfolioChartWithCardsTemplateForCreditFacility>
  );
};
